import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { doc, setDoc, updateDoc, serverTimestamp, collection, getDocs, query, where, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { signUpWithEmailPassword } from '../../services/authService';
import { linkPhoneToUid } from '../../services/userProfileService';
import { IoChevronBack, IoSearchOutline, IoCloseCircle, IoCheckmarkCircle } from 'react-icons/io5';

const USER_TYPES = [
  { key: 'teacher', label: '강사', icon: '' },
  { key: 'student', label: '학생', icon: '' },
  { key: 'assistant', label: '조교', icon: '' },
  { key: 'parent', label: '학부모', icon: '' },
];

const SignUpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // PhoneVerifyPage에서 전달받은 데이터
  const verifiedPhone = location.state?.verifiedPhone || '';
  const fromSocial = location.state?.fromSocial || false;
  const socialEmail = location.state?.socialEmail || '';
  const socialName = location.state?.socialName || '';
  const socialUid = location.state?.socialUid || '';

  // 전화번호 인증 안 됐으면 돌려보내기
  useEffect(() => {
    if (!verifiedPhone) {
      navigate('/phone-verify', { replace: true });
    }
  }, [verifiedPhone, navigate]);

  const [selectedType, setSelectedType] = useState('student');
  const [form, setForm] = useState({
    name: socialName,
    loginId: '',
    password: '',
    passwordConfirm: '',
    school: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 아이디 중복 체크
  const [idChecked, setIdChecked] = useState(false);
  const [idChecking, setIdChecking] = useState(false);
  const [idAvailable, setIdAvailable] = useState(false);

  // 학부모 - 학생 검색
  const [studentSearch, setStudentSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // 학생 - 클래스 선택
  const [classList, setClassList] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [classLoading, setClassLoading] = useState(false);
  const [classSheetOpen, setClassSheetOpen] = useState(false);

  useEffect(() => {
    loadClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadClasses = async () => {
    if (classList.length > 0) return;
    setClassLoading(true);
    try {
      const snap = await getDocs(collection(db, 'classes'));
      setClassList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('클래스 목록 로드 실패:', err);
    } finally {
      setClassLoading(false);
    }
  };

  const toggleClass = (cls) => {
    setSelectedClasses((prev) =>
      prev.find((c) => c.id === cls.id)
        ? prev.filter((c) => c.id !== cls.id)
        : [...prev, cls]
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (name === 'loginId') {
      setIdChecked(false);
      setIdAvailable(false);
    }
  };

  const handleCheckId = async () => {
    if (!form.loginId.trim()) return;
    setIdChecking(true);
    try {
      const q = query(collection(db, 'users'), where('loginId', '==', form.loginId.trim()));
      const snap = await getDocs(q);
      setIdChecked(true);
      setIdAvailable(snap.empty);
    } catch (err) {
      console.error('중복 체크 실패:', err);
    } finally {
      setIdChecking(false);
    }
  };

  const handleStudentSearch = async () => {
    if (!studentSearch.trim()) return;
    setSearching(true);
    setShowResults(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const keyword = studentSearch.trim().toLowerCase();
      const students = snap.docs
        .map((d) => ({ uid: d.id, ...d.data() }))
        .filter((s) => s.role === 'student')
        .filter((s) =>
          (s.name || '').toLowerCase().includes(keyword) ||
          (s.email || '').toLowerCase().includes(keyword)
        );
      setSearchResults(students);
    } catch (err) {
      console.error('학생 검색 실패:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setShowResults(false);
    setStudentSearch('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name) {
      setError('이름을 입력해주세요.');
      return;
    }
    if (!fromSocial) {
      if (!form.loginId) { setError('아이디를 입력해주세요.'); return; }
      if (!idChecked || !idAvailable) { setError('아이디 중복 확인을 해주세요.'); return; }
      if (!form.password) { setError('비밀번호를 입력해주세요.'); return; }
      if (form.password !== form.passwordConfirm) { setError('비밀번호가 일치하지 않습니다.'); return; }
      if (form.password.length < 6) { setError('비밀번호는 6자 이상이어야 합니다.'); return; }
    }
    if (selectedType === 'parent' && !selectedStudent) {
      setError('수강 학생을 선택해주세요.');
      return;
    }

    setLoading(true);
    try {
      let uid;

      if (fromSocial) {
        // 소셜 로그인 유저 - 이미 Firebase Auth에 있음
        uid = socialUid;
      } else {
        // 아이디 기반 회원가입 (Firebase Auth에는 fake email 사용)
        const fakeEmail = `${form.loginId.trim()}@classmanage.local`;
        const firebaseUser = await signUpWithEmailPassword({ email: fakeEmail, password: form.password });
        uid = firebaseUser.uid;
      }

      const userData = {
        loginId: fromSocial ? '' : form.loginId.trim(),
        email: fromSocial ? socialEmail : '',
        name: form.name,
        role: selectedType,
        phoneE164: verifiedPhone,
        phoneVerified: true,
        status: 'pending',
        profileImage: '',
        createdAt: serverTimestamp(),
      };

      if (selectedType === 'parent') {
        userData.children = [selectedStudent.uid];
        userData.childrenNames = [selectedStudent.name];
      } else if (selectedType === 'student') {
        userData.school = form.school;
        userData.classes = selectedClasses.map((c) => c.id);
        userData.classNames = selectedClasses.map((c) => c.name);
      }

      await setDoc(doc(db, 'users', uid), userData);
      await linkPhoneToUid({ uid, phoneE164: verifiedPhone });

      // 학생이 선택한 클래스에 studentIds 추가
      if (selectedType === 'student' && selectedClasses.length > 0) {
        for (const cls of selectedClasses) {
          await updateDoc(doc(db, 'classes', cls.id), {
            studentIds: arrayUnion(uid),
          });
        }
      }

      navigate('/login', {
        replace: true,
        state: { toast: '회원가입이 완료되었습니다.\n관리자 승인 후 이용 가능합니다.' },
      });
    } catch (err) {
      console.error('회원가입 실패:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('이미 사용 중인 아이디입니다.');
      } else {
        setError('회원가입에 실패했습니다. 다시 시도해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate(-1)}>
          <IoChevronBack size={24} />
        </BackButton>
        <HeaderTitle>회원가입</HeaderTitle>
      </Header>

      <FormArea onSubmit={handleSubmit}>
        <Label>사용자 유형</Label>
        <TypeGrid>
          {USER_TYPES.map((type) => (
            <TypeButton
              key={type.key}
              type="button"
              $active={selectedType === type.key}
              onClick={() => {
                setSelectedType(type.key);
                setSelectedStudent(null);
                setSearchResults([]);
                setShowResults(false);
                setSelectedClasses([]);
              }}
            >
              <TypeEmoji>{type.icon}</TypeEmoji>
              <TypeLabel>{type.label}</TypeLabel>
            </TypeButton>
          ))}
        </TypeGrid>

        <Label>이름</Label>
        <Input
          name="name"
          placeholder=""
          value={form.name}
          onChange={handleChange}
        />

        {!fromSocial && (
          <>
            <Label>아이디</Label>
            <IdRow>
              <IdInput
                name="loginId"
                type="text"
                placeholder=""
                value={form.loginId}
                onChange={handleChange}
              />
              <DupCheckBtn type="button" onClick={handleCheckId} disabled={idChecking || !form.loginId.trim()}>
                {idChecking ? '확인중' : '중복확인'}
              </DupCheckBtn>
            </IdRow>
            {idChecked && (
              <DupResult $ok={idAvailable}>
                {idAvailable ? '사용 가능한 아이디입니다.' : '이미 사용 중인 아이디입니다.'}
              </DupResult>
            )}

            <Label>비밀번호</Label>
            <Input
              name="password"
              type="password"
              placeholder=""
              value={form.password}
              onChange={handleChange}
            />

            <Label>비밀번호 확인</Label>
            <Input
              name="passwordConfirm"
              type="password"
              placeholder=""
              value={form.passwordConfirm}
              onChange={handleChange}
            />
          </>
        )}


        {/* 학생: 클래스 선택 + 소속 */}
        {selectedType === 'student' && (
          <>
            <Label>소속 학교/학원</Label>
            <Input
              name="school"
              placeholder=""
              value={form.school}
              onChange={handleChange}
            />

            <Label>클래스 선택</Label>
            <ClassSelectBtn type="button" onClick={() => setClassSheetOpen(true)}>
              {selectedClasses.length > 0
                ? `${selectedClasses.map(c => c.name).join(', ')} (${selectedClasses.length}개)`
                : '클래스를 선택하세요'}
            </ClassSelectBtn>

            {classSheetOpen && (
              <SheetOverlay onClick={() => setClassSheetOpen(false)}>
                <SheetContent onClick={(e) => e.stopPropagation()}>
                  <SheetHandle />
                  <SheetTitle>클래스 선택</SheetTitle>
                  {classLoading ? (
                    <ResultEmpty>불러오는 중...</ResultEmpty>
                  ) : classList.length === 0 ? (
                    <ResultEmpty>등록된 클래스가 없습니다</ResultEmpty>
                  ) : (
                    <SheetList>
                      {classList.map((cls) => {
                        const isSelected = selectedClasses.find((c) => c.id === cls.id);
                        return (
                          <SheetItem
                            key={cls.id}
                            type="button"
                            $selected={!!isSelected}
                            onClick={() => toggleClass(cls)}
                          >
                            <SheetItemLeft>
                              <ClassName>{cls.name}</ClassName>
                              {cls.teacherName && <ClassInfo>{cls.teacherName}</ClassInfo>}
                              {cls.schedule && cls.schedule.length > 0 && (
                                <ClassInfo>
                                  {cls.schedule.map((s) => `${s.day} ${s.time}`).join(', ')}
                                </ClassInfo>
                              )}
                            </SheetItemLeft>
                            {isSelected && <IoCheckmarkCircle size={22} color="#4A7CFE" />}
                          </SheetItem>
                        );
                      })}
                    </SheetList>
                  )}
                  <SheetDoneBtn type="button" onClick={() => setClassSheetOpen(false)}>
                    선택 완료
                  </SheetDoneBtn>
                </SheetContent>
              </SheetOverlay>
            )}
          </>
        )}

        {/* 학부모: 학생 선택 */}
        {selectedType === 'parent' && (
          <>
            <Label>수강 학생 선택</Label>
            {selectedStudent ? (
              <SelectedStudentChip>
                <StudentInfo>
                  <StudentName>{selectedStudent.name}</StudentName>
                  {selectedStudent.email && <StudentId>{selectedStudent.email}</StudentId>}
                </StudentInfo>
                <RemoveBtn type="button" onClick={() => setSelectedStudent(null)}>
                  <IoCloseCircle size={20} />
                </RemoveBtn>
              </SelectedStudentChip>
            ) : (
              <SearchWrap>
                <SearchRow>
                  <SearchInput
                    placeholder="학생 이름으로 검색"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleStudentSearch(); } }}
                  />
                  <SearchBtn type="button" onClick={handleStudentSearch} disabled={searching}>
                    <IoSearchOutline size={18} />
                    검색
                  </SearchBtn>
                </SearchRow>
                {showResults && (
                  <ResultsList>
                    {searching ? (
                      <ResultEmpty>검색 중...</ResultEmpty>
                    ) : searchResults.length === 0 ? (
                      <ResultEmpty>검색 결과가 없습니다</ResultEmpty>
                    ) : (
                      searchResults.map((s) => (
                        <ResultItem key={s.uid} onClick={() => handleSelectStudent(s)}>
                          <ResultName>{s.name}</ResultName>
                          {s.email && <ResultId>{s.email}</ResultId>}
                          {s.school && <ResultSchool>{s.school}</ResultSchool>}
                        </ResultItem>
                      ))
                    )}
                  </ResultsList>
                )}
              </SearchWrap>
            )}
          </>
        )}

        {error && <ErrorText>{error}</ErrorText>}

        <SubmitButton type="submit" disabled={loading}>
          {loading ? '가입 중...' : '회원가입'}
        </SubmitButton>
      </FormArea>
    </Container>
  );
};

export default SignUpPage;

const Container = styled.div`
  min-height: 100vh;
  background: #F5F7FA;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 8px;
  background: white;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HeaderTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
`;

const FormArea = styled.form`
  padding: 16px 24px 40px;
  margin: 16px;
  background: white;
  border-radius: 16px;
  border: 1px solid #E5E7EB;
`;

const VerifiedPhoneBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #EEF2FF;
  border-radius: 8px;
  font-size: 14px;
  color: #3B6DE8;
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
  margin-top: 16px;
`;

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const TypeButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.border};
  background: ${({ $active, theme }) => $active ? theme.colors.primaryLight : 'white'};
  gap: 4px;
`;

const TypeEmoji = styled.span`
  font-size: 24px;
`;

const TypeLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const IdRow = styled.div`
  display: flex;
  gap: 8px;
`;

const IdInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
  background: white;
  &::placeholder { color: ${({ theme }) => theme.colors.text.hint}; }
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const DupCheckBtn = styled.button`
  padding: 0 14px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
  &:disabled { opacity: 0.5; }
`;

const DupResult = styled.p`
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
  color: ${({ $ok }) => $ok ? '#22C55E' : '#EF4444'};
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
  background: white;
  &::placeholder { color: ${({ theme }) => theme.colors.text.hint}; }
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const DisabledInput = styled.div`
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
  background: #f5f5f5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SearchWrap = styled.div`
  position: relative;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 8px;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSize.md};
  &::placeholder { color: ${({ theme }) => theme.colors.text.hint}; }
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const SearchBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 12px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
  &:disabled { opacity: 0.6; }
`;

const ResultsList = styled.div`
  margin-top: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: white;
  max-height: 200px;
  overflow-y: auto;
`;

const ResultItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  &:last-child { border-bottom: none; }
  &:hover { background: ${({ theme }) => theme.colors.primaryLight}; }
`;

const ResultName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ResultId = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary};
`;

const ResultSchool = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ResultEmpty = styled.div`
  padding: 16px;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.hint};
`;

const SelectedStudentChip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const StudentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StudentName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StudentId = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary};
`;

const RemoveBtn = styled.button`
  color: ${({ theme }) => theme.colors.text.hint};
  display: flex;
  &:hover { color: ${({ theme }) => theme.colors.accent.red}; }
`;

const ClassSelectBtn = styled.button`
  width: 100%;
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: white;
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SheetOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const SheetContent = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 70vh;
  background: white;
  border-radius: 16px 16px 0 0;
  padding: 12px 20px 24px;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.25s ease-out;
  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`;

const SheetHandle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #ddd;
  margin: 0 auto 12px;
`;

const SheetTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
`;

const SheetList = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const SheetItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 12px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
  background: ${({ $selected }) => $selected ? '#F0F4FF' : 'transparent'};
  &:last-child { border-bottom: none; }
`;

const SheetItemLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const SheetDoneBtn = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  border-radius: 28px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: white;
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const ClassName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ClassInfo = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.accent.red};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-top: 8px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: white;
  font-size: ${({ theme }) => theme.fontSize.md};
  margin-top: 24px;
  &:disabled { opacity: 0.6; }
`;
